import { createContext, ReactElement, ReactNode, useContext, useEffect, useState, useMemo, useCallback } from 'react';

import useUserProfile from '../dataHooks/useUserProfile';
import Person from '../types/Person';

import { useAuth, AuthUser } from './AuthProvider';

export interface DataProviderProps {
  children?: ReactNode;
}

type UserState = {
  status:
    | 'unknown'
    | 'logged_out'
    | 'authenticating_user'
    | 'authenticating_profile'
    | 'authenticating_memberships'
    | 'ready'
    | 'error';
  error?: Error;
  authUser: AuthUser | null | undefined;
  userProfile: Person | null | undefined;
};

export interface DataContextModel {
  // User state
  userState: UserState;

  // God mode settings
  isAdminDisabled: boolean;
  adminDisabled: (disable: boolean) => void;

  // Membership checks
  isSelf: (person: Person) => boolean;

  // Capability checks
  canViewPersonDetails: (person: Person) => boolean;
  canEditPerson: (person: Person) => boolean;
  canEditPersonDetails: (person: Person) => boolean;
}

export const DataContext = createContext<DataContextModel>({} as DataContextModel);

export function useData(): DataContextModel {
  return useContext(DataContext);
}

export const DataProvider = ({ children }: DataProviderProps): ReactElement => {
  const { isGod, isLoggingIn, authUser } = useAuth();

  const [userState, setUserState] = useState<UserState>({
    status: 'unknown',
    authUser: undefined,
    userProfile: undefined,
  });

  const {
    userProfile: fetchedUserProfile,
    isUserProfileLoading,
    userProfileError,
  } = useUserProfile({ userProfileId: authUser?.profileId ?? undefined });

  const [isAdminDisabled, setIsAdminDisabled] = useState<boolean>(false);

  useEffect(() => {
    // Step 1: Check authentication
    if (!authUser) {
      setUserState({
        status: isLoggingIn ? 'authenticating_user' : 'logged_out',
        authUser: null,
        userProfile: null,
      });
      return;
    }

    // Step 2: Load profile
    if (isUserProfileLoading) {
      setUserState({
        status: 'authenticating_profile',
        authUser: authUser,
        userProfile: null,
      });
      return;
    }

    // Check profile error
    if (userProfileError) {
      console.error('DataProvider - Profile error:', userProfileError.message);
      setUserState({
        status: 'error',
        error: userProfileError,
        authUser: authUser,
        userProfile: null,
      });
      return;
    }

    // Profile must be loaded to continue
    if (!fetchedUserProfile) {
      return; // Wait for profile to load
    }

    // Step 4: Everything is ready
    if (fetchedUserProfile) {
      setUserState({
        status: 'ready',
        authUser: authUser,
        userProfile: fetchedUserProfile,
      });
    }
  }, [
    authUser,
    isLoggingIn,
    fetchedUserProfile,
    isUserProfileLoading,
    userProfileError,
  ]);
  
  // *************** Getter functions ****************

  // *************** Membership checks ****************
    const adminDisabled = useCallback((disable: boolean) => {
      setIsAdminDisabled(disable);
    }, []);

  const isSelf = useCallback(
    (profile: Person) => {
      return userState.userProfile?.itemId === profile.itemId;
    },
    [userState.userProfile]
  );

  // *************** Capability checks ****************
  const canEditPerson = useCallback(
    (profile: Person): boolean => {
      return isGod || isSelf(profile);
    },
    [isGod, isSelf]
  );

  const canEditPersonDetails = useCallback(
    (profile: Person): boolean => {
      return isGod || isSelf(profile);
    },
    [isGod, isSelf]
  );

  const canViewPersonDetails = useCallback(
    (profile: Person): boolean => {
      if (isGod || isSelf(profile)) {
        return true;
      } else {
        return false;
      }
    },
    [isGod, isSelf]
  );


  const values = useMemo(() => {
    return {
      // User state
      userState,
      // God mode settings
      isAdminDisabled,
      adminDisabled,
      
      // Membership checks
      isSelf,

      // Capability checks
      canViewPersonDetails,
      canEditPerson,
      canEditPersonDetails,

    };
  }, [
    userState,
    isAdminDisabled,
    adminDisabled,
    canViewPersonDetails,
    canEditPerson,
    canEditPersonDetails,
    isSelf,
  ]);

  return <DataContext.Provider value={values}>{children}</DataContext.Provider>;
};
